/**
 * How the chat links to the records the API talks about.
 *
 * The chat lives in `share/`, which knows nothing about timber. The records
 * it shows come from the API, and their fields point at other records: a log
 * points at its cutting, a shipment at its buyer, a cutting at its provider.
 *
 * To show those as a name and a link to the record's page in the web app,
 * the chat needs to know what kind of thing each field points at and how to
 * render it. That knowledge belongs to the package that owns the records, so
 * it is injected rather than imported: the application wires the registry in,
 * and the chat asks for it through a context.
 *
 * A chat with no registry still works. It shows the plain value instead of a
 * link, which is worse but not broken -- so a package that is not wired in
 * takes the chat down to plain text rather than to nothing.
 */
import * as React from 'react';

/** The props a registered entity component takes. */
export interface ChatEntityLinkProps {
  /** The id of the record to show. */
  id: string;
}

/** What the chat needs to know about one kind of record. */
export interface ChatEntityDescriptor {
  /**
   * Renders the record's name, linked to its page in the web app.
   *
   * The application already has one of these per record type: the same
   * component a list row uses, so a name in the chat looks like a name
   * everywhere else.
   */
  Link: React.ComponentType<ChatEntityLinkProps>;
}

/**
 * The records the chat can link to, by kind.
 *
 * The kinds are the ones the API names its fields after: `cuttingId` is a
 * cutting, `storageId` is a timber storage.
 */
export type ChatEntityRegistry = Record<string, ChatEntityDescriptor>;

const ChatEntityContext = React.createContext<ChatEntityRegistry>({});

export const ChatEntityProvider: React.FC<{
  registry: ChatEntityRegistry;
  children: React.ReactNode;
}> = ({ registry, children }) => (
  <ChatEntityContext.Provider value={registry}>{children}</ChatEntityContext.Provider>
);

/** The records this chat can link to. */
export const useChatEntityRegistry = (): ChatEntityRegistry =>
  React.useContext(ChatEntityContext);
